// File: webelieve-frontend/src/features/dms/components/EnhancedDocumentFilters.tsx
// Enhanced Document Filters Component for EdgeLex DMS
// Provides advanced filtering with classification support

import React, { useState } from 'react';
import {
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import type { DMSFilterOptionsResponse, DMSFilterOption } from '../DocumentAPI';

interface EnhancedDocumentFiltersProps {
  // Search functionality
  searchQuery: string;
  onSearchChange: (query: string) => void;
  totalDocuments: number;
  
  // Enhanced filters
  filters: {
    // Basic filters (compatible with existing system)
    status?: 'Active' | 'Archived' | 'deleted';
    sortBy?: 'title' | 'created_at' | 'updated_at' | 'size' | 'type';
    sortOrder?: 'asc' | 'desc';
    dateFrom?: string;
    dateTo?: string;
    categoryId?: string;
    
    // NEW: Unassigned documents filter
    assignmentStatus?: 'all' | 'assigned' | 'unassigned' | 'no_client' | 'no_matter';
    
    // Enhanced classification filters
    document_type?: string;
    document_subtype?: string;
    language?: string;
    phase?: string;
    practice_area?: string;
    jurisdiction?: string;
    urgency?: string;
    classification_method?: string;
    date_range?: string;
    confidence_min?: number;
    confidence_max?: number;
  };
  onFiltersChange: (filters: any) => void;
  onClearFilters: () => void;
  filterOptions?: DMSFilterOptionsResponse | null;
  className?: string;
}

const EnhancedDocumentFilters: React.FC<EnhancedDocumentFiltersProps> = ({
  searchQuery,
  onSearchChange,
  totalDocuments,
  filters,
  onFiltersChange,
  onClearFilters,
  filterOptions,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [searchInput, setSearchInput] = useState(searchQuery);

  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  React.useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Debounced search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, onSearchChange]);

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {};
    setLocalFilters(clearedFilters);
    setSearchInput('');
    onClearFilters();
  };

  const getActiveFiltersCount = () => {
    return Object.entries(filters).filter(([key, value]) => {
      if (value === undefined || value === '') return false;
      if (key === 'assignmentStatus' && value === 'all') return false;
      return true;
    }).length;
  };

  const asOptions = (values: string[]): DMSFilterOption[] =>
    values.map((value) => ({
      value,
      label: value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
    }));

  const documentTypes = filterOptions?.documentTypes || [];
  const languages = filterOptions?.languages || asOptions(['en', 'es', 'fr', 'de', 'it', 'pt']);
  const phases = filterOptions?.phases || asOptions(['intake', 'review', 'approved', 'active', 'closed', 'rejected']);
  const practiceAreas = filterOptions?.practiceAreas || [];
  const jurisdictions = filterOptions?.jurisdictions || [];
  const urgencyLevels = filterOptions?.urgencyLevels || asOptions(['low', 'medium', 'high', 'urgent']);
  const classificationMethods = filterOptions?.classificationMethods || asOptions(['manual', 'rule_based', 'machine_learning', 'hybrid']);
  const dateRanges = asOptions(['today', 'yesterday', 'last_7_days', 'last_30_days', 'last_90_days', 'this_year', 'last_year', 'custom']);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Bar */}
      <div className="bg-[var(--brand-surface-card)] rounded-lg border border-[var(--brand-border)] shadow-sm p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-[var(--brand-text-tertiary)]" />
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-[var(--brand-border)] rounded-md leading-5 bg-[var(--brand-surface-panel)] text-[var(--brand-text-primary)] placeholder:text-[var(--brand-text-tertiary)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-focus-ring)] focus:border-[var(--brand-accent-primary)] sm:text-sm"
              placeholder="Search documents by title, content, or classification..."
            />
          </div>
          {getActiveFiltersCount() > 0 && (
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-4 py-2 border border-[var(--brand-border)] rounded-md shadow-sm text-sm font-medium text-[var(--brand-text-secondary)] bg-[var(--brand-surface-panel)] hover:bg-[var(--brand-surface-hover)] hover:text-[var(--brand-text-primary)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-focus-ring)]"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Reset All
            </button>
          )}
        </div>
        
        {/* Results Count */}
        <div className="mt-3 text-sm text-[var(--brand-text-secondary)]">
          Showing {totalDocuments} documents
        </div>
      </div>

      {/* Enhanced Filters Panel */}
      <div className={`bg-[var(--brand-surface-card)] rounded-lg border border-[var(--brand-border)] shadow-sm`}>
        {/* Header */}
        <div className="p-4 border-b border-[var(--brand-border)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FunnelIcon className="w-5 h-5 text-[var(--brand-text-secondary)] mr-2" />
              <h3 className="text-lg font-semibold text-[var(--brand-text-primary)]">
                Document Filters
              </h3>
              {getActiveFiltersCount() > 0 && (
                <span className="ml-2 px-2 py-1 text-xs font-medium bg-[var(--brand-accent-primary-soft)] text-[var(--brand-accent-deep)] rounded-full">
                  {getActiveFiltersCount()} active
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="inline-flex items-center px-3 py-1.5 border border-[var(--brand-border)] rounded-md text-sm font-medium text-[var(--brand-text-secondary)] bg-[var(--brand-surface-panel)] hover:bg-[var(--brand-surface-hover)] hover:text-[var(--brand-text-primary)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-focus-ring)]"
              >
                {isExpanded ? (
                  <>
                    <ChevronUpIcon className="w-4 h-4 mr-1" />
                    Collapse
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="w-4 h-4 mr-1" />
                    Expand
                  </>
                )}
              </button>
              {getActiveFiltersCount() > 0 && (
                <button
                  onClick={handleClearFilters}
                  className="inline-flex items-center px-3 py-1.5 border border-[var(--brand-border)] rounded-md text-sm font-medium text-[var(--brand-text-secondary)] bg-[var(--brand-surface-panel)] hover:bg-[var(--brand-surface-hover)] hover:text-[var(--brand-text-primary)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-focus-ring)]"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Content */}
        {isExpanded && (
          <div className="p-4 space-y-6">
            {/* Basic Filters */}
            <div>
              <h4 className="text-sm font-semibold text-[var(--brand-text-primary)] mb-3">Basic Filters</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={localFilters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Archived">Archived</option>
                    <option value="deleted">Deleted</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sort By
                  </label>
                  <select
                    value={localFilters.sortBy || 'updated_at'}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="updated_at">Last Updated</option>
                    <option value="created_at">Created Date</option>
                    <option value="title">Title</option>
                    <option value="size">File Size</option>
                    <option value="type">Type</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sort Order
                  </label>
                  <select
                    value={localFilters.sortOrder || 'desc'}
                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date From
                  </label>
                  <input
                    type="date"
                    value={localFilters.dateFrom || ''}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date To
                  </label>
                  <input
                    type="date"
                    value={localFilters.dateTo || ''}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Advanced Classification Filters */}
            <div>
              <h4 className="text-sm font-semibold text-[var(--brand-text-primary)] mb-3">Advanced Classification</h4>
            
              {/* Document Type and Subtype */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Document Type
                  </label>
                  <select
                    value={localFilters.categoryId || ''}
                    onChange={(e) => handleFilterChange('categoryId', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Types</option>
                    {documentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Document Subtype
                  </label>
                  <input
                    type="text"
                    value={localFilters.document_subtype || ''}
                    onChange={(e) => handleFilterChange('document_subtype', e.target.value || undefined)}
                    placeholder="Enter subtype..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Language and Phase */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Language
                  </label>
                  <select
                    value={localFilters.language || ''}
                    onChange={(e) => handleFilterChange('language', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Languages</option>
                    {languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Legal Phase
                  </label>
                  <select
                    value={localFilters.phase || ''}
                    onChange={(e) => handleFilterChange('phase', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Phases</option>
                    {phases.map((phase) => (
                      <option key={phase.value} value={phase.value}>
                        {phase.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Practice Area and Jurisdiction */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Practice Area
                  </label>
                  <select
                    value={localFilters.practice_area || ''}
                    onChange={(e) => handleFilterChange('practice_area', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Practice Areas</option>
                    {practiceAreas.map((area) => (
                      <option key={area.value} value={area.value}>
                        {area.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Jurisdiction
                  </label>
                  <select
                    value={localFilters.jurisdiction || ''}
                    onChange={(e) => handleFilterChange('jurisdiction', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Jurisdictions</option>
                    {jurisdictions.map((jurisdiction) => (
                      <option key={jurisdiction.value} value={jurisdiction.value}>
                        {jurisdiction.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Urgency and Classification Method */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Urgency Level
                  </label>
                  <select
                    value={localFilters.urgency || ''}
                    onChange={(e) => handleFilterChange('urgency', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Urgency Levels</option>
                    {urgencyLevels.map((urgency) => (
                      <option key={urgency.value} value={urgency.value}>
                        {urgency.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Classification Method
                  </label>
                  <select
                    value={localFilters.classification_method || ''}
                    onChange={(e) => handleFilterChange('classification_method', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Methods</option>
                    {classificationMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Date Range and Confidence */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Date Range
                  </label>
                  <select
                    value={localFilters.date_range || ''}
                    onChange={(e) => handleFilterChange('date_range', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Dates</option>
                    {dateRanges.map((range) => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Min Confidence
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={localFilters.confidence_min || ''}
                    onChange={(e) => handleFilterChange('confidence_min', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max Confidence
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={localFilters.confidence_max || ''}
                    onChange={(e) => handleFilterChange('confidence_max', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="100"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Assignment Status Filter */}
            <div>
              <h4 className="text-sm font-semibold text-[var(--brand-text-primary)] mb-3">Assignment Status</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assignment Status
                </label>
                <select
                  value={localFilters.assignmentStatus || 'all'}
                  onChange={(e) => handleFilterChange('assignmentStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="all">All Documents</option>
                  <option value="assigned">Fully Assigned (Client + Matter)</option>
                  <option value="unassigned">⚠️ Unassigned Documents</option>
                  <option value="no_client">📋 No Client Assignment</option>
                  <option value="no_matter">📁 No Matter Assignment</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedDocumentFilters; 
